// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * ColorUtils.js
 * Centralized color utility functions for the Starship Console
 * Provides consistent color coding across all components
 */

import GameConfig from '../../config/GameConfig';

/**
 * Get color based on combat threat level
 * @param {string} threat - Threat level: 'HIGH', 'MEDIUM', 'LOW'
 * @returns {string} Hex color code
 */
export const getThreatColor = (threat) => {
  switch (threat) {
    case 'HIGH': return '#ff4444';
    case 'MEDIUM': return '#ffaa00';
    case 'LOW': return '#00ff88';
    default: return '#00ffff';
  }
};

/**
 * Get color based on navigation threat level (more granular scale)
 * @param {string} threat - Threat level: 'MINIMAL', 'LOW', 'MODERATE', 'HIGH', 'EXTREME'
 * @returns {string} Hex color code
 */
export const getNavigationThreatColor = (threat) => {
  switch (threat) {
    case 'MINIMAL': return '#00ff88';
    case 'LOW': return '#88ff00';
    case 'MODERATE': return '#ffaa00';
    case 'HIGH': return '#ff4444';
    case 'EXTREME': return '#ff0000';
    default: return '#00ffff';
  }
};

/**
 * Get color based on resource level percentage
 * @param {number} percentage - Resource level (0-100)
 * @returns {string} Hex color code
 */
export const getResourceColor = (percentage) => {
  if (percentage >= 80) return '#00ff88'; // green
  if (percentage >= 60) return '#ffaa00'; // orange
  return '#ff4444'; // red
};

/**
 * Get color for resource level (categorical scale)
 * @param {string} resources - Resource level: 'VERY HIGH', 'HIGH', 'MODERATE', 'LOW', 'MINIMAL', 'NONE'
 * @returns {string} Hex color code
 */
export const getResourceLevelColor = (resources) => {
  switch (resources) {
    case 'VERY HIGH': return '#00ff88';
    case 'HIGH': return '#88ff00';
    case 'MODERATE': return '#ffaa00';
    case 'LOW': return '#ff8800';
    case 'MINIMAL': return '#ff4444';
    case 'NONE': return '#666666';
    default: return '#00ffff';
  }
};

/**
 * Get color based on weapon status
 * @param {string} status - Weapon status: 'READY', 'ARMED', 'CHARGING', 'RECHARGING', 'OFFLINE', 'DEPLETED'
 * @returns {string} Hex color code
 */
export const getWeaponStatusColor = (status) => {
  switch (status) {
    case 'READY': return '#00ff88';
    case 'ARMED': return '#ffff00';
    case 'CHARGING': return '#ffaa00';
    case 'RECHARGING': return '#ff8800';
    case 'OFFLINE': return '#ff4444';
    case 'DEPLETED': return '#ff4444';
    default: return '#00ffff';
  }
};

/**
 * Get color for system status
 * @param {string} status - System status: 'OPTIMAL', 'NOMINAL', 'CAUTION', 'WARNING', 'DEGRADED', 'CRITICAL', 'STANDBY', 'RECALIBRATING', 'OFFLINE'
 * @returns {string} Hex color code
 */
export const getSystemStatusColor = (status) => {
  switch (status) {
    case 'OPTIMAL': return '#00ff88';
    case 'NOMINAL': return '#00ffff';
    case 'CAUTION': return '#88ff00';
    case 'WARNING': return '#ffaa00';
    case 'DEGRADED': return '#ffaa00';
    case 'CRITICAL': return '#ff4444';
    case 'STANDBY': return '#00aaff';
    case 'RECALIBRATING': return '#ff8800';
    case 'OFFLINE': return '#888888';
    default: return '#00ffff';
  }
};

/**
 * Get color for alert severity
 * @param {string} severity - Alert severity: 'INFO', 'WARNING', 'CRITICAL'
 * @returns {string} Hex color code
 */
export const getAlertColor = (severity) => {
  switch (severity) {
    case 'INFO': return '#00ffff';
    case 'WARNING': return '#ffaa00';
    case 'CRITICAL': return '#ff4444';
    default: return '#00ffff';
  }
};

/**
 * Get color based on percentage value using GameConfig thresholds
 * @param {number} percentage - Value (0-100)
 * @returns {string} Hex color code
 */
export const getPercentageColor = (percentage) => {
  if (percentage > GameConfig.ui.statusColors.optimalThreshold) return '#00ff88'; // green
  if (percentage > GameConfig.ui.statusColors.warningThreshold) return '#ffaa00'; // orange
  return '#ff4444'; // red
};
